import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';

/** 305: one photo reference as the inspection payload publishes it (`items[].photos`). */
export interface InspectionPhoto {
  id: string;
  /** `null` when the platform could not vouch for the file — never shown as an image. */
  url: string | null;
  /** What this particular photo is of (its finding's area). Wins over the lightbox `title`, so
   *  stepping from one finding's photo to the next finding's retitles the view (Gate A finding). */
  label?: string;
}

interface PhotoLightboxProps {
  photos: InspectionPhoto[];
  startIndex: number;
  onClose: () => void;
  /** What these photos are of — the finding's area, or the inspection. */
  title: string;
  /** 307: let the owner decide what a failed image means (e.g. fetch a fresh signed link once). When
   *  given, the lightbox reports failures instead of marking the photo unavailable itself. */
  onPhotoError?: (photoId: string) => void;
  /** 307: the owner's view of which photos are definitively unavailable. */
  isPhotoBroken?: (photoId: string) => boolean;
}

/**
 * 305 (US1, FR-002): an inspection's photos, one at a time and large enough to judge damage by.
 * Built on the shared `Modal`, which already owns Escape, the backdrop and the close button, so
 * this adds only stepping: ←/→ and two buttons, stopping at the ends rather than wrapping (a
 * reviewer comparing photos should know when they have seen the last one).
 */
export function PhotoLightbox({
  photos,
  startIndex,
  onClose,
  title,
  onPhotoError,
  isPhotoBroken,
}: PhotoLightboxProps) {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(startIndex, 0), Math.max(photos.length - 1, 0)),
  );
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

  const atStart = index <= 0;
  const atEnd = index >= photos.length - 1;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, photos.length - 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [photos.length]);

  const photo = photos[index];
  const broken = (id: string) => (isPhotoBroken ? isPhotoBroken(id) : brokenIds.has(id));
  const showable = photo?.url && !broken(photo.id);
  const heading = photo?.label ?? title;

  return (
    <Modal open onClose={onClose} title={heading} size="xl">
      <div className="flex flex-col gap-3">
        <div className="flex min-h-64 items-center justify-center rounded-lg bg-surface">
          {showable ? (
            <img
              data-testid="lightbox-image"
              src={photo.url ?? undefined}
              alt={`${heading}, ${index + 1} of ${photos.length}`}
              className="max-h-[70vh] w-full rounded-lg object-contain"
              onError={() =>
                onPhotoError
                  ? onPhotoError(photo.id)
                  : setBrokenIds((prev) => new Set(prev).add(photo.id))
              }
            />
          ) : (
            <p data-testid="lightbox-unavailable" className="text-sm text-muted">
              Photo unavailable
            </p>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Button
            size="sm"
            variant="secondary"
            aria-label="Previous photo"
            disabled={atStart}
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
          >
            ‹ Previous
          </Button>
          <span data-testid="lightbox-position" className="text-sm text-steel">
            Photo {index + 1} of {photos.length}
          </span>
          <Button
            size="sm"
            variant="secondary"
            aria-label="Next photo"
            disabled={atEnd}
            onClick={() => setIndex((i) => Math.min(i + 1, photos.length - 1))}
          >
            Next ›
          </Button>
        </div>
      </div>
    </Modal>
  );
}
